import { useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Share2, RotateCcw } from "lucide-react";
import { BRAND } from "@/styles/brand";
import { toast } from "sonner";

const C = BRAND.colors;

// Animated scanning ring around QR code - proves it's live, not a screenshot
const ScanRing = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    {/* Rotating dashed ring */}
    <motion.div
      className="absolute"
      style={{ width: 160, height: 160 }}
      animate={{ rotate: 360 }}
      transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
    >
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r="75" fill="none" stroke="#4A7C59" strokeWidth="2"
          strokeDasharray="12 8" opacity="0.5" />
      </svg>
    </motion.div>
    {/* Pulsing inner ring */}
    <motion.div
      className="absolute rounded-full"
      style={{ width: 145, height: 145, border: "2px solid rgba(255,255,255,0.3)" }}
      animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Corner brackets */}
    {[
      { top: 12, left: 12, borderTop: "3px solid #fff", borderLeft: "3px solid #fff" },
      { top: 12, right: 12, borderTop: "3px solid #fff", borderRight: "3px solid #fff" },
      { bottom: 12, left: 12, borderBottom: "3px solid #fff", borderLeft: "3px solid #fff" },
      { bottom: 12, right: 12, borderBottom: "3px solid #fff", borderRight: "3px solid #fff" },
    ].map((style, i) => (
      <motion.div
        key={i}
        className="absolute"
        style={{ ...style, width: 20, height: 20 }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
      />
    ))}
    {/* Scanning line */}
    <motion.div
      className="absolute left-5 right-5"
      style={{ height: 2, background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)" }}
      animate={{ top: ["25%", "75%", "25%"] }}
      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
);

export default function MemberCardDisplay({ cardId, cardImage, memberCode, userName, qrValue, size = "full" }) {
  const [flipped, setFlipped] = useState(false);

  const handleShare = async () => {
    const shareData = { title: "Franky's Farm Mitgliedskarte", text: `${userName} ist Mitglied bei Franky's Farm! Code: ${memberCode}`, url: window.location.origin };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else { await navigator.clipboard.writeText(`Franky's Farm Member: ${memberCode}`); toast.success("Code kopiert!"); }
    } catch (e) {}
  };

  const handleFlip = () => {
    setFlipped(!flipped);
    if (navigator.vibrate) navigator.vibrate(5);
  };

  const isCompact = size === "compact";
  const cardW = isCompact ? "w-52" : "w-full max-w-xs";
  const cardH = isCompact ? "h-36" : "h-52";

  return (
    <div className={`${cardW} mx-auto`}>
      {/* Card */}
      <div className="relative" style={{ perspective: "1200px" }}>
        <motion.div
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.7, type: "spring", damping: 18 }}
          style={{ transformStyle: "preserve-3d" }}
          onClick={handleFlip}
          className={`${cardH} cursor-pointer relative`}
        >
          {/* === FRONT === */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{ backfaceVisibility: "hidden", boxShadow: "0 8px 30px rgba(0,0,0,0.18)" }}>
            {cardImage ? (
              <img src={cardImage} alt={cardId || "Mitgliedskarte"} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #2D5A3D, #4A7C59)" }}>
                <span className="text-white text-4xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {userName?.charAt(0) || "F"}
                </span>
              </div>
            )}
            {/* Shine overlay */}
            <motion.div className="absolute inset-0 pointer-events-none"
              style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 40%, rgba(255,255,255,0.1) 100%)" }}
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            {/* Name bar at bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-3"
              style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.65))" }}>
              <p className="text-white text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {userName || "Farm Member"}
              </p>
              <p className="text-white/70 text-xs" style={{ fontFamily: "'Figtree', sans-serif" }}>{memberCode}</p>
            </div>
          </div>

          {/* === BACK - QR Code with animated ring === */}
          <div className="absolute inset-0 rounded-2xl overflow-hidden flex flex-col items-center justify-center"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              background: "linear-gradient(145deg, #2D5A3D, #1E3D2A)",
              boxShadow: "0 8px 30px rgba(0,0,0,0.18)",
            }}>

            {/* Animated scan ring around QR */}
            <div className="relative" style={{ width: 160, height: 160 }}>
              <ScanRing />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="p-3 rounded-xl bg-white">
                  <QRCodeSVG
                    value={qrValue || memberCode}
                    size={isCompact ? 75 : 100}
                    fgColor="#2D5A3D"
                    bgColor="transparent"
                  />
                </div>
              </div>
            </div>

            <p className="text-sm font-bold mt-2 text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {memberCode}
            </p>
            <p className="text-xs mt-0.5 text-white/60" style={{ fontFamily: "'Figtree', sans-serif" }}>
              An der Kasse vorzeigen
            </p>

            {/* Live indicator */}
            <div className="absolute top-3 right-3 flex items-center gap-1.5 bg-white/10 rounded-full px-2 py-1">
              <motion.div
                className="w-2 h-2 rounded-full bg-green-400"
                animate={{ opacity: [1, 0.3, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              />
              <span className="text-[9px] text-white/70 font-medium" style={{ fontFamily: "'Figtree', sans-serif" }}>LIVE</span>
            </div>

            {/* Flip back hint */}
            <div className="absolute top-3 left-3 bg-white/10 rounded-full p-1.5">
              <RotateCcw size={12} color="rgba(255,255,255,0.6)" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* "Klicken fuer QR Code" hint under card */}
      <motion.p
        className="text-center mt-3 text-[13px] font-semibold"
        style={{ color: "#2D5A3D", fontFamily: "'Figtree', sans-serif" }}
        animate={!flipped ? { opacity: [0.5, 1, 0.5] } : { opacity: 1 }}
        transition={!flipped ? { duration: 2, repeat: Infinity } : {}}
      >
        {flipped ? "Zurück zur Karte tippen" : "👆 Klicken fuer QR-Code"}
      </motion.p>

      {/* Share button */}
      <motion.button whileTap={{ scale: 0.95 }} onClick={handleShare}
        className="mt-2 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
        style={{ backgroundColor: "rgba(45,90,61,0.1)", color: "#2D5A3D", fontFamily: "'Figtree', sans-serif" }}>
        <Share2 size={16} /> Karte teilen
      </motion.button>
    </div>
  );
}
